import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { formatPrice } from '../utils/formatters';
import { markInstagramPostUnavailable } from '../utils/instagramAvailability';
import { getCarDetailPath } from '../utils/carDetails';

const AutomovelBox = ({
    carId,
    carIndex = 0,
    carImage,
    title,
    price,
    kms,
    year,
    vendido,
    brevemente,
    isReadOnly,
    instagramShortcode,
    preferThumbnail = false,
    onUnavailable,
}) => {
    const [hidden, setHidden] = useState(false);
    const [imgFailed, setImgFailed] = useState(false);

    const detailPath = getCarDetailPath({ id: carId, instagramShortcode, titulo: title }, carIndex);
    const showEmbed = Boolean(instagramShortcode) && !preferThumbnail && !imgFailed;
    const isSold = Number(vendido) === 1;
    const isSoon = Number(brevemente) === 1;

    const handleImageError = () => {
        if (!instagramShortcode) return;
        if (preferThumbnail) {
            markInstagramPostUnavailable(instagramShortcode);
            setHidden(true);
            onUnavailable?.(instagramShortcode);
            return;
        }
        setImgFailed(true);
    };

    if (hidden) return null;

    const meta = [year && year !== '—' ? year : null, kms && kms !== '0' ? `${kms} km` : null]
        .filter(Boolean)
        .join(' · ');

    return (
        <Link
            to={detailPath}
            onClick={() => window.scrollTo(0, 0)}
            aria-label={`Ver ${title} — ${formatPrice(price)}`}
            className={`ajp-bracket group relative flex flex-col bg-[#0d0d0d] border border-white/[0.06] overflow-hidden cursor-pointer transition-all duration-500 hover:border-red/30 hover:shadow-card-hover h-full w-full no-underline ${
                isReadOnly ? 'opacity-90' : ''
            }`}
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-black">
                {isSold && (
                    <div className="absolute inset-0 z-30 bg-black/75 flex items-center justify-center pointer-events-none">
                        <span className="px-5 py-2 border border-red text-red font-bold text-[10px] uppercase tracking-[0.25em]">
                            Vendido
                        </span>
                    </div>
                )}

                {isSoon && !isSold && (
                    <div className="absolute top-0 left-0 z-30 pointer-events-none">
                        <span className="inline-block px-3 py-1.5 bg-red text-black font-bold text-[9px] uppercase tracking-[0.2em]">
                            Em Breve
                        </span>
                    </div>
                )}

                {showEmbed ? (
                    <>
                        <iframe
                            src={`https://www.instagram.com/p/${instagramShortcode}/embed/`}
                            className="instagram-iframe-crop border-0 bg-black pointer-events-none"
                            scrolling="no"
                            allowtransparency="true"
                            allow="encrypted-media"
                            title={title}
                            loading="lazy"
                            tabIndex={-1}
                        />
                        <span className="absolute inset-0 z-20" aria-hidden="true" />
                    </>
                ) : carImage ? (
                    <img
                        src={carImage}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                        onError={handleImageError}
                    />
                ) : (
                    <div className="w-full h-full bg-[#111]" />
                )}

                <div className="absolute inset-0 z-[15] bg-gradient-to-t from-black/90 via-black/20 to-transparent pointer-events-none" />

                <div className="absolute bottom-0 left-0 right-0 z-[25] p-4 sm:p-5 pointer-events-none">
                    <div className="font-display text-2xl sm:text-3xl text-red tracking-wide">
                        {formatPrice(price)}
                    </div>
                </div>
            </div>

            <div className="p-4 sm:p-5 flex flex-col flex-grow min-h-[100px]">
                <div className="ajp-line w-8 mb-3 group-hover:w-14 transition-all duration-500" />

                <h3 className="font-body text-white font-semibold text-sm sm:text-[15px] leading-snug mb-1 group-hover:text-red transition-colors duration-300 line-clamp-2">
                    {title}
                </h3>

                {meta && (
                    <p className="text-white/30 text-[10px] uppercase tracking-widest mb-3">{meta}</p>
                )}

                <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/[0.06]">
                    <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/25 group-hover:text-red/70 transition-colors">
                        Ver detalhes
                    </span>
                    <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-white/10 text-white/40 group-hover:bg-red group-hover:border-red group-hover:text-black transition-all duration-300">
                        <FaArrowRight size={11} />
                    </div>
                </div>
            </div>
        </Link>
    );
};

export default AutomovelBox;
